import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  useOutsideClick,
  Portal,
  IconButton,
} from "@chakra-ui/react";
import { IoEllipsisVertical } from "react-icons/io5";
import {
  FiCopy,
  FiTrash2,
  FiMap,
  FiDownload,
  FiEdit,
  FiSettings,
} from "react-icons/fi";

/**
 * AgendaActionMenu - Context menu for agenda cards using custom Portal implementation
 * Shows options: Edit, Remix, Export, Rename, Duplicate, Blueprint, Delete.
 * Edit is hidden for past agendas (read-only).
 */
const AgendaActionMenu = ({
  isOpen = false,
  onClose,
  onEdit,
  onRemix,
  onRename,
  onBlueprint,
  onDuplicate,
  onExportPptx,
  isExportingPptx = false,
  onDelete,
  position = { x: 0, y: 0 },
  anchorRef,
  blueprintDisabled = false,
  isPastAgenda = false,
  onMoreOptionsClick,
  ...props
}) => {
  const menuRef = React.useRef();

  // Close menu when clicking outside (but not on the anchor button)
  useOutsideClick({
    ref: menuRef,
    handler: (event) => {
      // Don't close if clicking on the anchor button
      if (anchorRef?.current && anchorRef.current.contains(event.target)) {
        return;
      }
      onClose();
    },
  });

  const menuItems = [
    ...(isPastAgenda
      ? []
      : [
          {
            id: "edit",
            label: "Edit",
            icon: FiSettings,
            onClick: onEdit,
            isIconComponent: true,
          },
        ]),
    {
      id: "remix",
      label: "Remix",
      icon: "🌀",
      onClick: onRemix,
      highlighted: true,
      disabled: isPastAgenda,
    },
    {
      id: "exportPptx",
      label: isExportingPptx ? "Exporting..." : "Export to PowerPoint",
      icon: FiDownload,
      onClick: onExportPptx,
      isIconComponent: true,
      disabled: isExportingPptx || isPastAgenda,
    },
    {
      id: "rename",
      label: "Rename",
      icon: FiEdit,
      onClick: onRename,
      isIconComponent: true,
      disabled: isPastAgenda,
      hasBorder: true,
    },
    {
      id: "duplicate",
      label: "Duplicate",
      icon: FiCopy,
      onClick: onDuplicate,
      isIconComponent: true,
    },
    {
      id: "blueprint",
      label: "Blueprint",
      icon: FiMap,
      onClick: onBlueprint,
      isIconComponent: true,
      disabled: blueprintDisabled,
    },
    {
      id: "delete",
      label: "Delete",
      icon: FiTrash2,
      onClick: onDelete,
      isIconComponent: true,
      danger: true,
      disabled: isPastAgenda,
    },
  ];

  const handleItemClick = (item, event) => {
    // Stop event propagation to prevent parent click handlers from firing
    event?.stopPropagation();

    if (item.disabled) {
      return; // Don't execute or close if disabled
    }
    if (item.onClick) {
      item.onClick(event);
    }
    onClose();
  };

  return (
    <>
      {/* Menu Button */}
      <IconButton
        ref={anchorRef}
        aria-label="More options"
        icon={<IoEllipsisVertical />}
        variant="ghost"
        size="sm"
        w="20px"
        h="20px"
        minW="20px"
        color="#000000"
        _hover={{ bg: "gray.100" }}
        onClick={onMoreOptionsClick}
        {...props}
      />

      {/* Menu Dropdown */}
      {isOpen && (
        <Portal>
          <Box
            ref={menuRef}
            position="fixed"
            top={`${Math.max(
              10,
              Math.min(position.y, window.innerHeight - 200)
            )}px`}
            left={`${position.x}px`}
            zIndex={1000}
            bg="white"
            borderRadius="10px"
            border="1px solid"
            borderColor="rgba(208, 204, 213, 0.3)"
            boxShadow="0px 4px 8px rgba(208, 204, 213, 0.16), 0px 0px 4px rgba(208, 204, 213, 0.08)"
            py={1}
            minW="133px"
            maxW="200px"
          >
            <VStack spacing={0} align="stretch">
              {menuItems.map((item) => (
                <Box key={item.id}>
                  <Box
                    px="8px"
                    py={item.highlighted ? "6px" : "10px"}
                    cursor={item.disabled ? "not-allowed" : "pointer"}
                    transition="all 0.2s"
                    _hover={
                      !item.disabled
                        ? {
                            bg: "#F6F5F7",
                          }
                        : {}
                    }
                    onClick={(event) => handleItemClick(item, event)}
                    opacity={item.disabled ? 0.5 : 1}
                  >
                    <HStack spacing="8px" align="center">
                      <Text
                        fontSize="12px"
                        fontWeight="400"
                        color={item.danger ? "#CC474E" : "#0C001C"}
                        lineHeight="1.5"
                        letterSpacing="-1%"
                        fontFamily="Geist, sans-serif"
                        flex="1"
                      >
                        {item.label}
                      </Text>

                      {item.isIconComponent ? (
                        <Box color={item.danger ? "#CC474E" : "#4C47CC"}>
                          <item.icon size="16px" strokeWidth="1.25px" />
                        </Box>
                      ) : item.icon ? (
                        <Text
                          fontSize="16px"
                          lineHeight="1.5"
                          fontFamily="Geist, sans-serif"
                        >
                          {item.icon}
                        </Text>
                      ) : null}
                    </HStack>
                  </Box>

                  {item.hasBorder && <Box h="1px" bg="#F6F5F7" mx="8px" />}
                </Box>
              ))}
            </VStack>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default AgendaActionMenu;
